/**
 * ClientMarketingDashboardPage — Sprint 42 C2.
 *
 * A per-client (per-workspace/tenant) view of marketing campaigns: the same
 * `/admin/marketing/campaigns` feed the Marketing Brain dashboard uses, grouped
 * by the campaign's `workspace_id` (Sprint 42 C1 tenant scoping). Campaigns
 * without a workspace — e.g. before C1 lands, or house/first-party campaigns —
 * group under a single "House" bucket, so this page is useful today and lights
 * up further once C1 attaches `workspace_id`.
 *
 * NEW page (does not edit the existing Marketing pages). Founder-gated, polls
 * every 30 s. The grouping/format helpers are exported pure functions so they
 * can be unit-tested without rendering React.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ClientCampaign {
  id: string;
  product_id: string;
  status: string;
  created_at: number;
  /** Sprint 42 C1 tenant scope. Optional — absent before C1 / for house campaigns. */
  workspace_id?: string | null;
  title_tag?: string | null;
}

interface ListCampaignsResponse {
  campaigns: ClientCampaign[];
  total: number;
}

/** One client's rolled-up marketing footprint. */
export interface ClientGroup {
  /** Raw workspace id, or `null` for the house bucket. */
  workspaceId: string | null;
  /** Human label for the bucket. */
  label: string;
  /** Campaigns belonging to this client. */
  campaigns: ClientCampaign[];
  /** Count per status, for the breakdown chips. */
  statusCounts: Record<string, number>;
}

// ── Pure helpers (exported for tests) ─────────────────────────────────────────

/** Bucket label for a workspace id. Empty/absent ⇒ the shared house bucket. */
export function clientLabel(workspaceId: string | null | undefined): string {
  if (workspaceId == null || workspaceId.trim() === '') return 'House (unassigned)';
  return workspaceId;
}

/**
 * Group campaigns by `workspace_id` (house bucket for absent ids), sorted by
 * descending campaign count then label so the busiest client leads. Pure.
 */
export function groupByClient(campaigns: ClientCampaign[]): ClientGroup[] {
  const byKey = new Map<string, ClientGroup>();
  for (const c of campaigns) {
    const hasWs = c.workspace_id != null && c.workspace_id.trim() !== '';
    const workspaceId = hasWs ? c.workspace_id!.trim() : null;
    const key = workspaceId ?? '__house__';
    let group = byKey.get(key);
    if (!group) {
      group = {
        workspaceId,
        label: clientLabel(workspaceId),
        campaigns: [],
        statusCounts: {},
      };
      byKey.set(key, group);
    }
    group.campaigns.push(c);
    group.statusCounts[c.status] = (group.statusCounts[c.status] ?? 0) + 1;
  }
  return [...byKey.values()].sort(
    (a, b) => b.campaigns.length - a.campaigns.length || a.label.localeCompare(b.label),
  );
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#7dd3fc',
  running: '#a78bfa',
  approved: '#6ee7b7',
  published: '#34d399',
  rejected: '#f87171',
  failed: '#f87171',
};

/** Status → accent color, falling back to subtle ink for unknown statuses. */
export function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

/** Format a Unix-seconds timestamp for the activity column. */
export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchCampaigns(): Promise<ListCampaignsResponse> {
  return apiFetch<ListCampaignsResponse>('/admin/marketing/campaigns');
}

function useClientCampaigns() {
  return useQuery({
    queryKey: ['marketing', 'campaigns', 'by-client'],
    queryFn: fetchCampaigns,
    refetchInterval: 30_000,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusChip({ status, count }: { status: string; count: number }) {
  return (
    <span
      style={{
        fontSize: '7.5pt',
        padding: '1px 6px',
        border: `1px solid ${statusColor(status)}`,
        color: statusColor(status),
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 700,
        borderRadius: 3,
      }}
    >
      {status} {count}
    </span>
  );
}

function ClientCard({ group }: { group: ClientGroup }) {
  const latest = [...group.campaigns].sort((a, b) => b.created_at - a.created_at)[0];
  return (
    <div
      style={{
        background: 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 18px',
        minWidth: 260,
        flex: '1 1 260px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span
          style={{
            flex: 1,
            fontSize: '10pt',
            fontWeight: 700,
            color: 'var(--omega-ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {group.label}
        </span>
        <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
          {group.campaigns.length} campaign{group.campaigns.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {Object.entries(group.statusCounts).map(([status, count]) => (
          <StatusChip key={status} status={status} count={count} />
        ))}
      </div>
      {latest && (
        <Link
          to={`/omega/marketing/campaigns/${latest.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: '8.5pt',
            color: 'var(--omega-ink-subtle)',
            textDecoration: 'none',
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Latest: {latest.title_tag ?? latest.product_id}
          </span>
          <span style={{ flexShrink: 0 }}>{fmtTime(latest.created_at)} →</span>
        </Link>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientMarketingDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useClientCampaigns();
  const campaigns = data?.campaigns ?? [];
  const groups = groupByClient(campaigns);
  const clientCount = groups.filter(g => g.workspaceId !== null).length;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Clients' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1
          style={{
            margin: 0,
            fontSize: '12pt',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Client Marketing
        </h1>
        <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
          {clientCount} client{clientCount !== 1 ? 's' : ''} · {campaigns.length} campaign
          {campaigns.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load client campaigns.</div>
      )}
      {!isPending && !isError && groups.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No campaigns yet — client campaigns appear here once a workspace runs marketing.
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {groups.map(g => (
          <ClientCard key={g.workspaceId ?? '__house__'} group={g} />
        ))}
      </div>
    </div>
  );
}
